import type * as React from 'react';

import {
    annotation,
    BLOCK_CODE_LINE,
    canInsertInlineIn,
    INLINE_ANNOTATION,
    INLINE_LINK,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_KEYBOARD,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import type { PaletteHandle } from '@gitbook/react-uikit';
import type { Value } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, Toolbar, ToolbarGroup } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { shortcuts } from '../../../Keyboard';
import { useContentReferenceContext } from '../../ContentReferences';
import { highlightSelection } from '../common';
import { useCompletionApi } from '../completion';
import { useColorPaletteFactory } from './colors';
import { toggleAnnotation, toggleLink, toggleMark } from './methods';

/**
 * Menu to format the selected range in the editor.
 */
export function FormatRangeMenu(props: {
    value: Value;
    editor: EditorContainer;
    palette: PaletteHandle;
}): React.ReactElement {
    const { value, editor, palette } = props;

    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();
    const createColorPalette = useColorPaletteFactory();

    const activeMarks = value.activeMarks.map((mark) => mark.type);

    const { startBlock, document } = value;
    assertIsDefined(startBlock);

    const isInFragment = Boolean(value.document.getClosestFragment(startBlock.key));
    const isInLink = value.hasInline(INLINE_LINK, isInFragment);
    const isInCodeBlock = startBlock.type === BLOCK_CODE_LINE;
    const canInsertMark = !isInCodeBlock;
    const canInsertLink = canInsertInlineIn({ document, block: startBlock }, INLINE_LINK);
    const canInsertAnnotation =
        canInsertInlineIn({ document, block: startBlock }, INLINE_ANNOTATION) &&
        annotation.isSelectionAnnotable(value, isInFragment);
    /*
     * Click to choose a color.
     */
    const wrapWithColor = () => {
        editor.change((change) => {
            highlightSelection(change);
        });
        palette.open(createColorPalette());
    };

    /*
     * Clear all formatting.
     */
    const clearFormatting = () => {
        editor.change((change) => {
            change.removeAllMarks().focus();
        });
    };

    return (
        <Toolbar aria-label="Format range">
            <AIToolbarGroup />
            {canInsertMark ? (
                <ToolbarGroup>
                    <Button
                        label="Bold"
                        leadingIcon={Icon.Bold}
                        active={activeMarks.has(MARK_BOLD)}
                        onPress={toggleMark({ type: MARK_BOLD, editor })}
                        tooltip={{ label: '*Bold*', shortcut: shortcuts.EDITOR_BOLD }}
                    />
                    <Button
                        label="Italic"
                        leadingIcon={Icon.Italic}
                        active={activeMarks.has(MARK_ITALIC)}
                        onPress={toggleMark({ type: MARK_ITALIC, editor })}
                        tooltip={{
                            label: '_Italic_',
                            shortcut: shortcuts.EDITOR_ITALIC,
                        }}
                    />
                    <Button
                        label="Strikethrough"
                        leadingIcon={Icon.Strikethrough}
                        active={activeMarks.has(MARK_STRIKETHROUGH)}
                        onPress={toggleMark({ type: MARK_STRIKETHROUGH, editor })}
                        tooltip={{
                            label: '~~Strikethrough~~',
                            shortcut: shortcuts.EDITOR_STRIKETHROUGH,
                        }}
                    />
                    <Button
                        label="Superscript"
                        leadingIcon={Icon.Superscript}
                        active={activeMarks.has(MARK_SUPERSCRIPT)}
                        onPress={toggleMark({ type: MARK_SUPERSCRIPT, editor })}
                        tooltip={{
                            label: 'Superscript',
                            shortcut: shortcuts.EDITOR_SUPERSCRIPT,
                        }}
                    />
                    <Button
                        label="Subscript"
                        leadingIcon={Icon.Subscript}
                        active={activeMarks.has(MARK_SUBSCRIPT)}
                        onPress={toggleMark({ type: MARK_SUBSCRIPT, editor })}
                        tooltip={{
                            label: 'Subscript',
                            shortcut: shortcuts.EDITOR_SUBSCRIPT,
                        }}
                    />
                    <Button
                        label="Code"
                        leadingIcon={Icon.Code}
                        active={activeMarks.has(MARK_CODE)}
                        onPress={toggleMark({ type: MARK_CODE, editor })}
                        tooltip={{
                            label: '`Code`',
                            shortcut: shortcuts.EDITOR_CODE,
                        }}
                    />
                    <Button
                        label="Keyboard"
                        leadingIcon={Icon.Keyboard}
                        active={activeMarks.has(MARK_KEYBOARD)}
                        onPress={toggleMark({ type: MARK_KEYBOARD, editor })}
                        tooltip={{
                            label: 'Keyboard',
                        }}
                    />
                </ToolbarGroup>
            ) : null}
            {canInsertAnnotation || canInsertLink ? (
                <ToolbarGroup>
                    {canInsertLink ? (
                        <Button
                            label="Link"
                            leadingIcon={Icon.Link}
                            onPress={toggleLink({
                                palette,
                                editor,
                                contentReferenceContext,
                                isInLink,
                                hiveClient,
                            })}
                            active={isInLink}
                            tooltip={{
                                label: 'Link',
                                shortcut: shortcuts.EDITOR_LINK,
                            }}
                        />
                    ) : null}
                    {canInsertAnnotation ? (
                        <Button
                            label="Annotate"
                            leadingIcon={Icon.Annotation}
                            onPress={toggleAnnotation({ editor })}
                            tooltip={{
                                label: 'Annotate',
                                description:
                                    'Allows you to add inline notes directly on the document',
                                shortcut: shortcuts.EDITOR_ANNOTATION,
                            }}
                        />
                    ) : null}
                </ToolbarGroup>
            ) : null}
            {canInsertMark ? (
                <ToolbarGroup>
                    <Button label="Color" leadingIcon={Icon.Highlight} onPress={wrapWithColor} />
                </ToolbarGroup>
            ) : null}
            <ToolbarGroup disabled={activeMarks.isEmpty()}>
                <Button
                    label="Clear formatting"
                    leadingIcon={Icon.PublishStatusUnpublished}
                    onPress={clearFormatting}
                />
            </ToolbarGroup>
        </Toolbar>
    );
}

function AIToolbarGroup(props: Partial<ButtonProps>) {
    const completionApi = useCompletionApi();
    if (!completionApi) {
        return null;
    }
    return (
        <ToolbarGroup>
            <Button
                {...props}
                label="Ask AI"
                leadingIcon={Icon.Magic}
                onPress={() => completionApi.open({ trigger: 'hovering-menu' })}
                tooltip={{
                    label: 'Ask AI',
                    description: 'Edit or generate with AI',
                    shortcut: shortcuts.EDITOR_AI,
                }}
            />
        </ToolbarGroup>
    );
}
